import jobServices from 'ApiService/Placement/jobServices';
import studentService from 'ApiService/Teacher/Student';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import { AuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';

const AddMockMarks = ({ classData, handleBack }) => {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [newSubjectName, setNewSubjectName] = useState('');
  const [isNewSubject, setIsNewSubject] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mockTestDate, setMockTestDate] = useState('');
  const [mockTestTime, setMockTestTime] = useState('');
  const [mockTestLink, setMockTestLink] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [assignToAll, setAssignToAll] = useState(false);
  const [isModalOpenConfirmation, setIsModalOpenConfirmation] = useState(false);
  const { state } = useContext(AuthContext);

  const getStudents = async () => {
    try {
      let response;
      if (state.user.role === 'account') {
        response = await jobServices.getPlacementReadyStudent(
          classData.courseId,
          classData.batchId
        );
      } else if (state.user.role === 'teacher') {
        response = await studentService.getStudent(
          classData.courseId,
          classData.batchId
        );
      }
      if (response.data.success) {
        const studentData = response.data.data.map((student) => ({
          id: student._id,
          name: student.name,
          instituteId: student.instituteId,
        }));
        setStudents(studentData);
      }
    } catch (error) {
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await studentService.getUniqueSubjects(
        classData.courseId,
        classData.batchId
      );
      if (response.data.success) {
        setSubjects(response.data.subjects);
      }
    } catch (error) {
      toast.error('Failed to fetch subjects');
    }
  };

  useEffect(() => {
    if (classData.courseId && classData.batchId) {
      getStudents();
      fetchSubjects();
    }
  }, [classData]);

  const handleStudentSelection = (studentId) => {
    if (assignToAll) {
      setAssignToAll(false);
    }

    setSelectedStudents((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleAssignToAll = () => {
    setAssignToAll((prev) => !prev);
    if (!assignToAll) {
      setSelectedStudents(students.map((student) => student.id));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleSubjectChange = (e) => {
    const value = e.target.value;
    if (value === 'new') {
      setIsNewSubject(true);
      setSelectedSubject('');
    } else {
      setIsNewSubject(false);
      setSelectedSubject(value);
      setNewSubjectName('');
    }
  };
  const handleConfirmSubmit = () => {
    if (!mockTestDate.trim()) {
      toast.error('Please select a mock test date');
      return;
    }

    if (!mockTestTime.trim()) {
      toast.error('Please select a mock test time');
      return;
    }

    if (selectedStudents.length === 0 && !assignToAll) {
      toast.error('Please select at least one student');
      return;
    }

    if (
      (!selectedSubject && !newSubjectName.trim()) ||
      (isNewSubject && !newSubjectName.trim())
    ) {
      toast.error('Please select or enter a subject name');
      return;
    }
    setIsModalOpenConfirmation(true);
  };
  const handleSubmit = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);
      const studentIds = assignToAll
        ? students.map((student) => student.id)
        : selectedStudents;

      const payload = {
        courseId: classData.courseId,
        batchId: classData.batchId,
        studentIds: studentIds,
        subjectName: isNewSubject ? newSubjectName : selectedSubject,
        testDate: mockTestDate,
        testTime: mockTestTime,
        testLink: mockTestLink,
      };

      const response = await jobServices.submitMockTest(payload); // Replace with your actual API call

      if (response.data.success) {
        toast.success(
          `Mock test details submitted successfully for ${studentIds.length} student(s)`
        );
        setIsModalOpenConfirmation(false);

        setMockTestLink('');
        setMockTestTime('');
        setMockTestDate('');
        setNewSubjectName('');
        fetchSubjects();
        getStudents();
        setAssignToAll(false);
        setSelectedStudents([]);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to submit mock test details'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <BallTriangle color="#5431ed" />
      </div>
    );
  }

  return (
    <Card extra={'w-full sm:overflow-auto p-4'}>
      <div className="mb-4 flex flex-col space-y-4">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-white/10">
              <th className="px-4 py-3 text-left text-xs font-bold">FIELD</th>
              <th className="px-4 py-3 text-left text-xs font-bold">VALUE</th>
            </tr>
          </thead>
          <tbody>
            {/* Student Selection */}
            <tr className="border-b border-gray-200 dark:border-white/10">
              <td className="px-4 py-3">Students</td>
              <td className="px-4 py-3">
                <div className="mb-2">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={assignToAll}
                      onChange={handleAssignToAll}
                      className="mr-2 rounded border-gray-300"
                    />
                    Assign to All Students
                  </label>
                </div>
                {!assignToAll && (
                  <div className="max-h-40 overflow-y-auto">
                    {students.map((student) => (
                      <div key={student.id} className="mb-1">
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedStudents.includes(student.id)}
                            onChange={() => handleStudentSelection(student.id)}
                            className="mr-2 rounded border-gray-300"
                          />
                          {student.name}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </td>
            </tr>

            {/* Subject Selection/Creation */}
            <tr className="border-b border-gray-200 dark:border-white/10">
              <td className="px-4 py-3">Subject</td>
              <td className="px-4 py-3">
                <select
                  value={isNewSubject ? 'new' : selectedSubject}
                  onChange={handleSubjectChange}
                  className="w-full rounded border px-3 py-2 dark:bg-navy-800"
                >
                  <option value="">Select Subject</option>
                  {subjects.map((subject) => (
                    <option key={subject} value={subject}>
                      {subject}
                    </option>
                  ))}
                  <option value="new">+ Add New Subject</option>
                </select>
                {isNewSubject && (
                  <input
                    type="text"
                    value={newSubjectName}
                    onChange={(e) => setNewSubjectName(e.target.value)}
                    className="mt-2 w-full rounded border px-3 py-2 dark:bg-navy-800"
                    placeholder="Enter new subject name"
                  />
                )}
              </td>
            </tr>

            {/* Test Date */}
            <tr className="border-b border-gray-200 dark:border-white/10">
              <td className="px-4 py-3">Test Date</td>
              <td className="px-4 py-3">
                <InputField
                  type="date"
                  value={mockTestDate}
                  onChange={(e) => setMockTestDate(e.target.value)}
                  className="w-full rounded border px-3 py-2 dark:bg-navy-800"
                />
              </td>
            </tr>

            {/* Test Time */}
            <tr className="border-b border-gray-200 dark:border-white/10">
              <td className="px-4 py-3">Test Time</td>
              <td className="px-4 py-3">
                <InputField
                  type="time"
                  value={mockTestTime}
                  onChange={(e) => setMockTestTime(e.target.value)}
                  className="w-full rounded border px-3 py-2 dark:bg-navy-800"
                />
              </td>
            </tr>

            {/* Test Link */}
            <tr className="border-b border-gray-200 dark:border-white/10">
              <td className="px-4 py-3">Test Link</td>
              <td className="px-4 py-3">
                <InputField
                  type="url"
                  value={mockTestLink}
                  onChange={(e) => setMockTestLink(e.target.value)}
                  className="w-full rounded border px-3 py-2 dark:bg-navy-800"
                  placeholder="https://example.com/test"
                />
              </td>
            </tr>
          </tbody>
        </table>

        {/* Selected Students Count */}
        <div className="px-4 text-sm text-gray-600 dark:text-gray-400">
          {assignToAll
            ? `Selected: All Students (${students.length})`
            : `Selected: ${selectedStudents.length} student(s)`}
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-col justify-between px-3 pt-2 md:flex-row md:px-8">
          <button
            onClick={handleBack}
            className="mb-3 cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20 md:mb-0"
          >
            Back
          </button>
          <button
            disabled={submitting}
            onClick={handleConfirmSubmit}
            className="linear cursor-pointer rounded-xl bg-brand-900 px-16 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
      <ConfirmationModal
        isOpen={isModalOpenConfirmation}
        onClose={() => {
          if (!submitting) setIsModalOpenConfirmation(false);
        }}
        onConfirm={handleSubmit}
        title="Confirm Mock Test Creation"
        message="Are you sure you want to create this mock test and share the link with selected students?"
        // confirmText="Yes, Proceed"
        confirmText={submitting ? 'Proceeding...' : 'Yes, Proceed'}
        cancelText="Cancel"
        icon="info"
        disableConfirm={submitting}
      />
    </Card>
  );
};

export default AddMockMarks;
